// Student-specific evidence for Professor Swan's phase transitions.
// This is a factual readout of the learner's SAVED responses, not a grade,
// inference about a physical experiment, or an AI-generated observation.
// Only use authored activity labels and student-entered values. Never expose
// answer keys, hidden tutor context, or unrevealed lesson content.

const clip = (value, limit = 120) => {
  const clean = String(value ?? "").replace(/\s+/g, " ").trim();
  if (clean.length <= limit) return clean;
  const cut = clean.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > limit / 2 ? cut.slice(0, lastSpace) : cut).trim()}…`;
};

const hasResponse = (value) =>
  value !== null &&
  value !== undefined &&
  (typeof value === "number" ||
    (typeof value === "string" && value.trim().length > 0));

const choiceLabel = (choices, answer) => {
  const matching = (Array.isArray(choices) ? choices : []).find(
    (choice) => choice?.id === answer
  );
  // Avoid reading internal choice IDs when no user-facing label is available.
  return matching?.label ? String(matching.label) : null;
};

const renderRecord = (block, record) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return null;
  }

  const fields = (block.fields || [])
    .map((field) => {
      const answer = record[field.id];
      if (!hasResponse(answer)) return null;
      const displayed = choiceLabel(field.choices, answer) || String(answer);
      return {
        key: String(field.id || ""),
        text: `${clip(field.label || field.id, 38)}: ${clip(displayed, 90)}`,
      };
    })
    .filter(Boolean);
  if (!fields.length) return null;
  // Favor actual measured outcomes over preliminary setup fields. Without
  // this, a rover record would quote the lane/surface but omit distance/time.
  const outcomeFields = fields.filter(({ key }) =>
    /result|distance|time|observation|evidence|before|after|pattern|travel|conclusion|theory|trial[12]/i.test(key)
  );
  const selected = outcomeFields.length ? outcomeFields : fields;
  return `${clip(block.title || "Your experiment", 55)} — ${selected
    .slice(0, 3)
    .map(({ text }) => text)
    .join("; ")}`;
};

const renderResponse = (block, saved) => {
  if (!hasResponse(saved)) return null;
  if (block.type === "evidence_response") {
    return `${clip(block.title || "Your explanation", 48)} — “${clip(saved, 160)}”`;
  }
  if (block.type === "choice_cards") {
    const label = choiceLabel(block.choices, saved);
    const responseKind = block.master_teacher_response_kind;
    const action = ["planned_change", "planned_investigation"].includes(responseKind)
      ? "Your proposed plan" : responseKind === "prediction" ? "Your prediction"
      : "Your selected answer";
    return label ? `${clip(block.title || action, 48)} (${action}) — “${clip(label, 125)}”` : null;
  }
  return null;
};

// Use only the saved evidence attached to completion-gated authored activities.
// Video/PWO-open flags, correctness checks and physical checklist ticks are
// deliberately excluded: they do not prove observations or experiment results.
export const buildGroundedPhaseRecap = (page, stepState) => {
  const blocks = Array.isArray(page?.blocks) ? page.blocks : [];
  const gate = blocks.find((block) => block?.type === "completion_gate");
  const rules = Array.isArray(gate?.rules) ? gate.rules : [];
  const saved = stepState && typeof stepState === "object" ? stepState : {};

  const evidence = rules
    .map((rule, order) => {
      const block = blocks.find((candidate) => candidate?.save_as === rule?.field);
      if (!block) return null;
      const value = saved[rule.field];
      if (block.type === "experiment_record") {
        const rendered = renderRecord(block, value);
        return rendered ? { order, kind: "record", rendered } : null;
      }
      if (["evidence_response", "choice_cards"].includes(block.type)) {
        const rendered = renderResponse(block, value);
        return rendered
          ? { order, kind: block.type === "choice_cards" ? "prediction" : "explanation", rendered }
          : null;
      }
      return null;
    })
    .filter(Boolean);

  if (!evidence.length) return "";

  // Preserve both early and late results in long Test & Improve phases instead
  // of quoting only the latest entry or accidentally implying improvement.
  const records = evidence.filter((item) => item.kind === "record");
  const selected = [];
  if (records.length > 0) selected.push(records[0]);
  if (records.length > 1) selected.push(records[records.length - 1]);
  const latestExplanation = [...evidence].reverse().find(
    (item) => item.kind === "explanation" && !selected.includes(item)
  );
  if (latestExplanation) selected.push(latestExplanation);
  if (!selected.length) selected.push(evidence[evidence.length - 1]);
  if (selected.length < 2) {
    const latestPrediction = [...evidence].reverse().find(
      (item) => item.kind === "prediction" && !selected.includes(item)
    );
    if (latestPrediction) selected.unshift(latestPrediction);
  }

  return `You recorded: ${selected
    .sort((left, right) => left.order - right.order)
    .map((item) => item.rendered)
    .join(". ")}.`;
};
